import { useState } from "react";

type Lang = "tr" | "en";

interface Props {
  lang: Lang;
  title: string;
  slug: string;
  isoDate?: string;
}

const L = {
  tr: {
    label: "Bu Yazıya Atıf Yap",
    apa: "APA",
    bibtex: "BibTeX",
    copy: "Kopyala",
    copied: "Kopyalandı",
  },
  en: {
    label: "Cite This Article",
    apa: "APA",
    bibtex: "BibTeX",
    copy: "Copy",
    copied: "Copied",
  },
} as const;

function buildApa(title: string, slug: string, isoDate?: string) {
  const year = isoDate ? new Date(isoDate).getFullYear() : new Date().getFullYear();
  const month = isoDate
    ? new Date(isoDate).toLocaleString("en-GB", { month: "long", day: "numeric" })
    : "";
  const url = `https://akillisurdurulebilirlik.com/yazilar/${slug}`;
  return `Öztaşkın, A. (${year}${month ? `, ${month}` : ""}). ${title}. Akıllı Sürdürülebilirlik. ${url}`;
}

function buildBibtex(title: string, slug: string, isoDate?: string) {
  const year = isoDate ? new Date(isoDate).getFullYear() : new Date().getFullYear();
  const key = `oztaskin${year}${slug.replace(/[^a-z0-9]/gi, "").slice(0, 20)}`;
  const url = `https://akillisurdurulebilirlik.com/yazilar/${slug}`;
  return `@misc{${key},
  author = {Öztaşkın, Arda},
  title  = {${title}},
  year   = {${year}},
  url    = {${url}},
  note   = {Akıllı Sürdürülebilirlik}
}`;
}

export function CiteButton({ lang, title, slug, isoDate }: Props) {
  const t = L[lang];
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<"apa" | "bibtex" | null>(null);
  const apa = buildApa(title, slug, isoDate);
  const bibtex = buildBibtex(title, slug, isoDate);

  const copy = async (kind: "apa" | "bibtex") => {
    const text = kind === "apa" ? apa : bibtex;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold hover:border-clay hover:text-clay transition-colors"
        aria-expanded={open}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9h4l-2 6H4l2-6zM16 9h4l-2 6h-4l2-6z" />
        </svg>
        {t.label}
      </button>

      {open && (
        <div className="mt-4 border border-border bg-paper/60 divide-y divide-border">
          {(["apa", "bibtex"] as const).map((kind) => (
            <div key={kind} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-clay font-bold">
                  {t[kind]}
                </span>
                <button
                  type="button"
                  onClick={() => copy(kind)}
                  className="text-[10px] uppercase tracking-[0.22em] font-bold border-b border-ink/25 hover:border-clay pb-0.5"
                >
                  {copied === kind ? t.copied : t.copy}
                </button>
              </div>
              <pre className="text-[12px] leading-relaxed text-ink/80 whitespace-pre-wrap font-mono m-0">
                {kind === "apa" ? apa : bibtex}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
